/**
 * A pagination breakdown is a data-only summary of page break data in
 * a script. It does the work of computing where dynamic page breaks
 * need to go and exposes information to allow rendering code to do
 * the appropriate thing. This class exposes:
 *
 * - Block IDs and prosemirror doc positions of manual and dynamic page breaks
 * - The page number associated with those page breaks (today that's just a
 *   naive count but evenually we can implement page locking/overridable
 *   numbering)
 * - Height information about pages (useful for simulating print previews)
 *
 * The inlinePaginationPlugin creates an instance of this class based on
 * editor state and uses it to create decoration widgets used on screen.
 *
 * Pages can be broken within the middle of a dialogue block. When this happens,
 * we add an additional property to the page object indicating a doc-level
 * offset where a decoration can be inserted to break the block. Based on the
 * remaining length of this split block, we subtract these lines from the
 * available maximum height of the following page.
 *
 * Printing code creates an instance of this class when converting a
 * snapshot to static html to inject css classes where page breaks should
 * occur.
 */

import { Node as PmNode } from 'prosemirror-model'
import { Decoration, DecorationSet } from 'prosemirror-view'

import { NodeTypeMap } from '@showrunner/codex'

import type { DocumentRanges } from '#choo-app/lib/editor/plugins/act-slug-tracker'

import {
  addBlockHeights,
  isCharacterFollower,
  PositionedBlockInfo,
} from './helpers'

const sectionHeadings: string[] = [
  NodeTypeMap.SCENE_HEADING,
  NodeTypeMap.NEW_ACT,
]

type PageInfo = {
  startPos: number
  endPos: number
  heightShortfall: number
}

export type PageDecorationInfo = PageInfo & {
  pageNumber: number
  pageAlias?: string
  isManualBreak: boolean
  isLockedPageBreak: boolean
  isEmptyPage: boolean
  isDialogueBreak: boolean
}

type OneOrMore<T> = [T, ...T[]]

// a ChunkOfBlocks is a set of one or more blocks that should stay together on the same page.
class ChunkOfBlocks {
  readonly blocks: PositionedBlockInfo[]

  constructor(first: PositionedBlockInfo) {
    this.blocks = [first]
  }

  get firstBlock(): PositionedBlockInfo {
    return this.blocks[0]
  }

  get lastBlock(): PositionedBlockInfo {
    return this.blocks[this.blocks.length - 1]
  }

  private get leadBlockType(): string {
    return this.firstBlock.blockType
  }

  private get canAppendToCharacterChunk() {
    const characterBlockIndex = this.blocks.findIndex(
      (b) => b.blockType === NodeTypeMap.CHARACTER,
    )

    return (
      characterBlockIndex > -1 &&
      this.blocks.slice(characterBlockIndex).length < 3
    )
  }

  private get isSceneHeadingChunk() {
    return this.leadBlockType === NodeTypeMap.SCENE_HEADING
  }

  get isManualBreak(): boolean {
    return this.firstBlock.manualBreak
  }

  get heightInfo() {
    return {
      contentHeight: addBlockHeights(this.blocks),
      spaceAbove: this.firstBlock.spaceAbove ?? 0,
    }
  }

  // determine if block can be added to this chunks
  private shouldAddBlock(block: PositionedBlockInfo): boolean {
    // never add a manual break to a chunk
    if (block.manualBreak) {
      return false
    }

    if (isCharacterFollower(block) && this.canAppendToCharacterChunk) {
      return true
    }

    if (
      this.isSceneHeadingChunk &&
      this.blocks.length === 1 &&
      block.blockType !== NodeTypeMap.SCENE_HEADING
    ) {
      return true
    }

    return false
  }

  // add block if appropriate and return true if added
  addBlock(block: PositionedBlockInfo): boolean {
    if (this.shouldAddBlock(block)) {
      this.blocks.push(block)
      return true
    }
    return false
  }

  matchingSplitPoints(splitPoints: DecorationSet) {
    const start = this.firstBlock.startPos
    const end = this.lastBlock.startPos + this.lastBlock.nodeSize
    return splitPoints.find(start, end, (spec) => spec.kind === 'inline')
  }
}

// A PageOfChunks is a set of one or more chunks that fit on a page.
abstract class PageOfChunks {
  protected readonly chunks: OneOrMore<ChunkOfBlocks>
  readonly maxLines: number

  constructor({
    maxLines,
    firstChunk,
  }: {
    maxLines: number
    firstChunk: ChunkOfBlocks
  }) {
    this.maxLines = maxLines
    this.chunks = [firstChunk]
  }

  get height(): number {
    return addBlockHeights(this.chunks.map((ch) => ch.heightInfo))
  }

  get viewLayoutInfo(): PageInfo {
    const { firstBlock } = this.chunks[0]
    const { lastBlock } = this.chunks[this.chunks.length - 1]

    return {
      startPos: firstBlock.startPos,
      endPos: lastBlock.startPos + lastBlock.nodeSize,
      heightShortfall: this.maxLines - this.height,
    }
  }

  get breakInfo(): {
    blockId: string
    isEmptyPage: boolean
    isLockedPageBreak: boolean
    isManualBreak: boolean
    startPos: number
    pageAlias?: string
  } {
    const { firstBlock } = this.chunks[0]

    const isEmptyPage = this.chunks.length === 1 && firstBlock.nodeSize === 2

    return {
      blockId: firstBlock.blockId,
      isManualBreak: firstBlock.manualBreak,
      isLockedPageBreak: firstBlock.lockedPageBreak,
      isEmptyPage,
      startPos: firstBlock.startPos,
      pageAlias: firstBlock.pageAlias,
    }
  }

  protected abstract shouldAddChunk(chunk: ChunkOfBlocks): boolean

  addChunk(chunk: ChunkOfBlocks): boolean {
    if (this.shouldAddChunk(chunk)) {
      this.chunks.push(chunk)
      return true
    }
    return false
  }
}

// @TODO: use more accurate dialogue height measurement in block height helpers
const NUMBER_OF_CHARS_IN_DIALOGUE_MAGIC_NUMBER = 34

class ComputedPage extends PageOfChunks {
  splitPoint?: number

  constructor(opts: { maxLines: number; firstChunk: ChunkOfBlocks }) {
    super(opts)
  }

  protected shouldAddChunk(chunk: ChunkOfBlocks): boolean {
    // never add a manual break
    if (chunk.isManualBreak) {
      return false
    }

    // check to see if there's room on page
    const projectedHeight =
      this.height + chunk.heightInfo.spaceAbove + chunk.heightInfo.contentHeight
    if (projectedHeight > this.maxLines) {
      return false
    }

    return true
  }

  addChunkWithOverflow(
    chunk: ChunkOfBlocks,
    splitPoints: DecorationSet,
    availableLines: number,
  ): { overflow: number } {
    // This is a (bad) approximation of the available space for dialogue to
    // split at, based on a hard-coded width
    const theoreticalLimitPos =
      chunk.firstBlock.startPos +
      availableLines * NUMBER_OF_CHARS_IN_DIALOGUE_MAGIC_NUMBER

    const splitPoint = nearestDecoration(
      chunk.matchingSplitPoints(splitPoints),
      theoreticalLimitPos,
    )

    if (!splitPoint) return { overflow: -1 }

    this.splitPoint = splitPoint.to
    this.chunks.push(chunk)

    const endPos = chunk.lastBlock.startPos + chunk.lastBlock.nodeSize
    const remainingLines =
      (endPos - splitPoint.to) / NUMBER_OF_CHARS_IN_DIALOGUE_MAGIC_NUMBER
    const overflow = Math.ceil(remainingLines) - 1 // account for character's name

    return { overflow }
  }

  get containsSectionHeading() {
    return this.chunks.some((c) =>
      c.blocks.some((b) => sectionHeadings.includes(b.blockType)),
    )
  }

  get startsSection() {
    return sectionHeadings.includes(this.chunks[0].firstBlock.blockType)
  }
}

interface PageBreakInfo {
  pageNumber: string
  blockId: string
  isLockedPageBreak: boolean
  isManualBreak: boolean
  isEmptyPage: boolean
  splitPoint?: number
  pageAlias?: string
  sceneContdHeader: boolean
  sceneContdFooter: boolean
  sceneNumber?: string
  sceneElementNumber?: string
  startPos: number
}

/**
 * Lays a document out into pages.
 *
 * It should be considered immutable, it performs the chunking work on
 * initialization.
 *
 * @param maxLines - The maximum number of lines per page
 * @param blocks - The blocks to paginate
 * @param splitPoints - Additional split points to consider within the blocks
 * @param documentRanges - Document ranges for scene tracking
 * @param titlePageBlocks - Optional title page blocks to paginate separately
 */
export class PaginationBreakdown {
  readonly chunks: ChunkOfBlocks[]
  readonly decorationData: PageDecorationInfo[]
  readonly titlePageBreakdown?: PaginationBreakdown
  private readonly splitPoints: DecorationSet
  private readonly pages: OneOrMore<ComputedPage>
  private readonly maxLines: number
  private readonly blocks: PositionedBlockInfo[]
  private readonly documentRanges: DocumentRanges | undefined
  readonly pageBreaks: PageBreakInfo[]

  constructor({
    maxLines,
    blocks,
    splitPoints,
    documentRanges,
    titlePageBlocks,
  }: {
    maxLines: number
    blocks: PositionedBlockInfo[]
    splitPoints?: DecorationSet
    documentRanges?: DocumentRanges
    titlePageBlocks?: PositionedBlockInfo[]
  }) {
    this.splitPoints = splitPoints ?? DecorationSet.empty
    this.maxLines = maxLines
    this.chunks = this.blocksToChunks(blocks)
    this.pages = this.computePages()
    this.blocks = blocks
    this.documentRanges = documentRanges
    this.decorationData = this.computeDecorationData()
    this.pageBreaks = this.buildPageBreaks()

    if (titlePageBlocks?.length) {
      this.titlePageBreakdown = new PaginationBreakdown({
        maxLines,
        blocks: titlePageBlocks,
      })
    }
  }

  private get firstChunk() {
    return this.chunks[0]
  }

  private computePages(): OneOrMore<ComputedPage> {
    const { maxLines: pageLines, firstChunk } = this

    // Normally, maxLines will be the same for each page in the breakdown. We
    // shorten it in the case that a page overflows and we create an inline
    // split point, so that we take into account the height of the overflow
    let maxLines = pageLines

    const computedPages: OneOrMore<ComputedPage> = [
      new ComputedPage({ maxLines, firstChunk }),
    ]
    this.chunks.slice(1).forEach((chunk) => {
      const lastComputedPage = computedPages[computedPages.length - 1]

      // If there's room, keep adding chunks to the page
      if (lastComputedPage.addChunk(chunk)) return

      // If not, try to find a split point in the current chunk
      const availableLines = lastComputedPage.maxLines - lastComputedPage.height

      // Only try to split the block if there's more than four lines worth of
      // widows (two for the character name and space above, two for dialogue)
      if (availableLines > 3) {
        const { overflow } = lastComputedPage.addChunkWithOverflow(
          chunk,
          this.splitPoints,
          availableLines,
        )

        if (overflow >= 0) {
          // We have just added a chunk larger than the height of the page, so
          // we need to limit the lines available to the next page
          maxLines -= overflow
          // continue and hit the "new page" case on the next chunk
          return
        }
      }

      // If there's no split point, start a new page
      computedPages.push(new ComputedPage({ maxLines, firstChunk: chunk }))
      maxLines = pageLines
    })

    return computedPages
  }

  private blocksToChunks(blocks: PositionedBlockInfo[]): ChunkOfBlocks[] {
    if (blocks.length === 0) {
      return []
    }

    const [first, ...rest] = blocks

    const chunks: ChunkOfBlocks[] = [new ChunkOfBlocks(first)]

    for (const block of rest) {
      const currentChunk = chunks[chunks.length - 1]
      const blockWasAdded = currentChunk.addBlock(block)

      // If there's still room, keep adding blocks to the current chunk
      if (blockWasAdded) continue

      // If not, let's consider it full and start a new chunk
      chunks.push(new ChunkOfBlocks(block))
    }

    return chunks
  }

  private computeDecorationData(): PageDecorationInfo[] {
    const result: PageDecorationInfo[] = []
    let foundLockedPage = false

    this.pages.forEach((page, index) => {
      const { isEmptyPage, isLockedPageBreak, isManualBreak, pageAlias } =
        page.breakInfo

      if (isLockedPageBreak) foundLockedPage = true

      if (page.splitPoint) {
        result.push({
          ...page.viewLayoutInfo,
          endPos: page.splitPoint,
          heightShortfall: 0,
          pageNumber: index + 1,
          isLockedPageBreak: false,
          isEmptyPage: false,
          isManualBreak: false,
          isDialogueBreak: true,
        })
      } else {
        result.push({
          ...page.viewLayoutInfo,
          pageNumber: index + 1,
          pageAlias,
          isEmptyPage,
          isLockedPageBreak,
          isManualBreak,
          isDialogueBreak: false,
        })
      }
    })

    const lastPage = this.pages[this.pages.length - 1]
    const lastDecorationInfo = result[result.length - 1]

    // If the last page in a document is a split dialogue, and the user hasn't
    // yet pressed "return" to create a new page, we add a fake final page to
    // give the page some height.
    if (lastDecorationInfo.isDialogueBreak) {
      result.push({
        startPos: lastDecorationInfo.endPos + 1,
        endPos: lastPage.viewLayoutInfo.endPos,
        heightShortfall: lastPage.maxLines,
        pageNumber: result.length + 1,
        isDialogueBreak: false,
        isEmptyPage: false,
        isLockedPageBreak: false,
        isManualBreak: false,
      })
    }

    return foundLockedPage ? deriveAliases(result) : result
  }

  get pageMap() {
    return new Map(
      this.decorationData.map((page) => [
        page.pageNumber,
        { empty: page.isEmptyPage, locked: page.isLockedPageBreak },
      ]),
    )
  }

  // used in printing to get block IDs and page numbers
  buildPageBreaks(): PageBreakInfo[] {
    let scenePageNumber = 0
    let previousPageEndScene: PmNode | undefined = undefined
    let previousPageStartScene: PmNode | undefined = undefined

    const mappedPages = this.pages.map((p, index) => {
      const currentPageStartScene = this.documentRanges?.getSceneAt(
        p.viewLayoutInfo.startPos,
      )
      // endPos is getting counted as part of the following page -- why??
      const currentPageEndScene = this.documentRanges?.getSceneAt(
        p.viewLayoutInfo.endPos - 1,
      )

      let sceneContdHeader = false
      let sceneContdFooter = false
      let sceneNumber: string | undefined = undefined
      let sceneElementNumber: string | undefined = undefined

      // Does this page start with a scene?
      if (p.startsSection) {
        // Page starts with a scene heading - this is a new scene, reset counter
        sceneContdHeader = false
        scenePageNumber = 0
      } else if (index > 0) {
        // Page doesn't start with scene heading - check if it continues from previous page

        if (
          previousPageEndScene &&
          previousPageEndScene === currentPageStartScene
        ) {
          // Scene continues from previous page
          sceneContdHeader = true
          sceneElementNumber = currentPageStartScene.attrs.elementNumber

          if (
            previousPageStartScene &&
            currentPageStartScene !== previousPageStartScene
          ) {
            scenePageNumber = 1
          } else {
            // Same scene as previous page start, increment counter
            scenePageNumber++
          }
        } else {
          // Scene doesn't continue from previous page
          sceneContdHeader = false
          scenePageNumber = 0
        }
      } else {
        // First page never has a continuation header
        sceneContdHeader = false
        scenePageNumber = 0
      }

      const nextPage = this.pages[index + 1]
      if (nextPage) {
        const nextPageStartScene = this.documentRanges?.getSceneAt(
          nextPage.viewLayoutInfo.startPos,
        )
        sceneContdFooter = Boolean(
          nextPageStartScene && currentPageEndScene === nextPageStartScene,
        )
      } else {
        sceneContdFooter = false
      }

      // Page 1: no header
      // Page 2: "CONTINUED:"
      // Page 3+: "CONTINUED: (2)", "(3)", etc.
      if (sceneContdHeader && scenePageNumber > 1) {
        sceneNumber = String(scenePageNumber)
      }

      previousPageStartScene = currentPageStartScene
      previousPageEndScene = currentPageEndScene

      return {
        ...p.breakInfo,
        splitPoint: p.splitPoint,
        pageNumber: String(index + 1),
        isLockedPageBreak: p.breakInfo.isLockedPageBreak,
        pageAlias: p.breakInfo.pageAlias,
        sceneContdHeader,
        sceneContdFooter,
        sceneNumber,
        sceneElementNumber,
      }
    })

    return mappedPages.some((p) => p.isLockedPageBreak)
      ? deriveAliases(mappedPages)
      : mappedPages
  }

  get pageCount(): number {
    return this.pages.length
  }

  get titlePageCount(): number {
    return this.titlePageBreakdown?.pageCount ?? 0
  }

  get blockIdsToOmit(): string[] {
    return this.blocks.filter((b) => b.omitFromPagination).map((b) => b.blockId)
  }

  pageIndexAtPosition(position: number): number {
    return this.decorationData.findIndex((page, i, arr) => {
      const nextPage = arr[i + 1]
      return (
        position >= page.startPos && (!nextPage || position < nextPage.startPos)
      )
    })
  }

  titlePageIndexAtPosition(position: number): number {
    return this.titlePageBreakdown?.pageIndexAtPosition(position) ?? -1
  }
}

/**
 * Find the decoration whose end position is closest to the given position
 */
function nearestDecoration(
  decorations: Decoration[],
  pos: number,
): Decoration | null {
  let closest: Decoration | null = null
  let closestDistance = Infinity

  // Decorations can be unsorted when returned from a decoration set, so we
  // can't do a binary search
  for (const decoration of decorations) {
    if (decoration.to <= pos) {
      const distance = pos - decoration.to

      if (distance < closestDistance) {
        closestDistance = distance
        closest = decoration
      }
    }
  }

  return closest
}

const createSuffix = (val: number) =>
  val < 26 ? String.fromCharCode(65 + val) : `Z${val - 25}`

export function deriveAliases<
  Type extends {
    isLockedPageBreak: boolean
    pageAlias?: string | undefined
  },
>(pages: Type[]): Type[] {
  const lockedAliases = pages
    .filter((p) => p.isLockedPageBreak)
    .map((p) => p.pageAlias)

  const lockedAliasSet = new Set(lockedAliases)
  const totalLockedCount = lockedAliasSet.size

  const aliasedPages = []
  let currLockedCount = 0
  let distance = 0
  let prevLockedAlias = '0'
  let modifier = ''

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i]

    if (page.isLockedPageBreak) {
      aliasedPages.push(page)
      prevLockedAlias = page.pageAlias ?? ''
      currLockedCount++
      distance = 0
      modifier = ''
      continue
    }

    // special case: page 1 is treated as if it were locked
    // in scripts with at least one subsequent locked page
    if (i === 0 && totalLockedCount > 0) {
      const pageAlias = '1'
      prevLockedAlias = pageAlias
      aliasedPages.push({ ...page, pageAlias })
      continue
    }

    // glean raw number from most recent locked page and increment by distance from that page
    const foundAll = currLockedCount === totalLockedCount
    const extractedNumber = prevLockedAlias.match(/\d+/)
    if (foundAll && extractedNumber) {
      const pageAlias = String(Number(extractedNumber[0]) + distance + 1)
      aliasedPages.push({ ...page, pageAlias })
      distance++
      continue
    }

    let keepDigging = true
    while (keepDigging) {
      const proposedAlias = prevLockedAlias + modifier + createSuffix(distance)
      if (lockedAliasSet.has(proposedAlias)) {
        modifier += 'a'
      } else {
        aliasedPages.push({ ...page, pageAlias: proposedAlias })
        distance++
        keepDigging = false
      }
    }
  }
  return aliasedPages
}
